import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { BetaBadge } from '@/components/ui/beta-badge';
import { Keyboard, Layers, LayoutGrid, Mic, MousePointer, Sun } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearch } from 'wouter';
import { LayoutsTab } from './tabs/LayoutsTab';
import { MicrophoneTab } from './tabs/MicrophoneTab';
import { OverlaysTab } from './tabs/OverlaysTab';
import { ShortcutsTab } from './tabs/ShortcutsTab';
import { ToolsTab } from './tabs/ToolsTab';
import { WLPresetsTab } from './tabs/WLPresetsTab';
const TABS = [
    { id: 'layouts', labelKey: 'viewer.tab.layouts', icon: LayoutGrid },
    { id: 'tools', labelKey: 'viewer.tab.tools', icon: MousePointer },
    { id: 'shortcuts', labelKey: 'viewer.tab.shortcuts', icon: Keyboard },
    { id: 'microphone', labelKey: 'viewer.tab.microphone', icon: Mic },
    { id: 'overlays', labelKey: 'viewer.tab.overlays', icon: Layers },
    { id: 'presets', labelKey: 'viewer.tab.wlPresets', icon: Sun },
];
const TAB_CONTENT = {
    layouts: LayoutsTab,
    tools: ToolsTab,
    shortcuts: ShortcutsTab,
    microphone: MicrophoneTab,
    overlays: OverlaysTab,
    presets: WLPresetsTab,
};
/**
 * Main Viewer Settings component with tabbed navigation
 */
export function ViewerSettings() {
    const { t } = useTranslation('settings');
    const searchString = useSearch();
    const viewerTabParam = useMemo(() => new URLSearchParams(searchString).get('viewerTab'), [searchString]);
    const [activeTab, setActiveTab] = useState('layouts');
    // Deep links (e.g. the dictation HUD's vocabulary-settings shortcut) select
    // a tab via ?viewerTab=; in-page clicks still own the state afterwards.
    useEffect(() => {
        if (viewerTabParam && TABS.some((tab) => tab.id === viewerTabParam)) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- one-way sync from the URL param
            setActiveTab(viewerTabParam);
        }
    }, [viewerTabParam]);
    const TabContent = TAB_CONTENT[activeTab];
    return (_jsxs("div", { children: [_jsx("h2", { className: "text-foreground mb-4 text-lg font-medium", children: t('viewer.title') }), _jsx("div", { role: "tablist", "aria-label": t('viewer.title'), className: "border-border mb-6 flex gap-1 overflow-x-auto border-b", children: TABS.map((tab) => {
                    const Icon = tab.icon;
                    return (_jsxs("button", { type: "button", role: "tab", id: `viewer-tab-${tab.id}`, "aria-selected": activeTab === tab.id, "aria-controls": `viewer-tabpanel-${tab.id}`, onClick: () => setActiveTab(tab.id), className: `-mb-px flex shrink-0 items-center gap-2 border-b-2 px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${activeTab === tab.id
                            ? 'border-primary text-foreground'
                            : 'text-muted-foreground hover:text-foreground border-transparent'}`, children: [_jsx(Icon, { size: 16 }), t(tab.labelKey), tab.id === 'microphone' && _jsx(BetaBadge, {})] }, tab.id));
                }) }), _jsx("div", { role: "tabpanel", id: `viewer-tabpanel-${activeTab}`, "aria-labelledby": `viewer-tab-${activeTab}`, children: _jsx(TabContent, {}) })] }));
}
